import React, { useState, useCallback, useEffect, useRef } from 'react';
import Grid from '@mui/material/Grid';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Autocomplete from '@mui/material/Autocomplete';
import InputAdornment from '@mui/material/InputAdornment';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import LinkIcon from '@mui/icons-material/Link';
import { DndProvider, useDrag, useDrop } from 'react-dnd'
import { HTML5Backend } from 'react-dnd-html5-backend'
import update from 'immutability-helper'
import { priceFormat, formatPriceToFloat } from '../helpers';
import { ajaxGetPartComponents } from '../services/partComponentService';
import { useNavigate } from 'react-router-dom';
import validator from 'validator';

function SelectPartDialog(props) {
  const { open, onClose, onSelect, currency, currencyRate, disabled } = props;
  const emptyPart = { part: null, part_name: '', price: 0, quantity: 1 };

  const [part, setPart] = useState(emptyPart);
  const [errors, setErrors] = useState({});
  const [availableParts, setAvailableParts] = useState([]);

  useEffect(() => {
    if (open && !disabled) {
      ajaxGetPartComponents()
        .then((res) => {
          const { data } = res;
          setAvailableParts(data);
        })
        .catch(() => {
          setAvailableParts([]);
        });
    }
  }, [open, disabled]);

  const handlePartChange = (_, value) => {
    if (value) {
      // if local currency is the same as the currency, use local price
      let price_UI = 0;
      if ( value.local_currency === currency && currency !== 'USD' ) {
        price_UI = value.local_price;
      } else {
        price_UI = formatPriceToFloat((value.price || 0) / (currencyRate || 1), 2, 6);
      }
      setPart({
        part: value.id,
        manuf_part_number: value.manuf_part_number,
        part_name: value.description || value.manuf_part_number,
        price: value.price || 0,
        price_UI: price_UI,
        quantity: 1,
        unit: value.unit || ''
      });
    }
  };

  const validateFields = useCallback(() => {
    let newErrors = {};
    let result = true;

    if (!part.part) {
      newErrors.part = 'required field';
      result = false;
    }
    if (!part.quantity || part.quantity <= 0) {
      newErrors.quantity = 'Please enter a valid quantity';
      result = false;
    }
    const priceValue = part.price !== undefined && part.price !== null ? part.price + '' : '';
    if (priceValue === '' || !validator.isFloat(priceValue)) {
      newErrors.price = 'Please enter a valid price';
      result = false;
    }
    
    setErrors(newErrors);
    return result;
  }, [part]);

  const handleSelect = () => {
    if (!validateFields()) return;
    onSelect(part);
    handleClose();
  };

  const handleClose = () => {
    setPart(emptyPart);
    setErrors({});
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      aria-labelledby="form-dialog-title"
    >
      <DialogTitle id="form-dialog-title">Add Part</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: '20px' }}>
          Select a part from the list.
        </DialogContentText>
        <Autocomplete
          size="small"
          options={availableParts.filter(p => p.active === 1)}
          getOptionLabel={(option) => `${option.description ? `[${option.manuf_part_number}] ${option.description}` : `[${option.manuf_part_number}] ${option.manuf_part_number}`}`}
          onChange={handlePartChange}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Manufacturer Part"
              error={!!errors.part}
              helperText={errors.part}
            />
          )}
        />
        <Grid container spacing={2} sx={{ mt: '20px' }}>
          <Grid item xs={6}>
            <TextField
              label={`Price in ${currency}`}
              size="small"
              type="number"
              value={part.price_UI !== undefined ? part.price_UI : part.price / (currencyRate || 1)}
              onChange={(e) => setPart({ ...part, price_UI: e.target.value, price: formatPriceToFloat((parseFloat(e.target.value) || 0) * (currencyRate || 1), 2, 6) })}
              error={!!errors.price}
              helperText={
                <>
                  {errors.price}
                  {currency !== 'USD' && (
                    <div style={{ color: 'rgba(0, 0, 0, 0.6)', fontSize: '0.75rem', marginTop: '3px' }}>
                      {`Price in USD: $${part.price}`}
                    </div>
                  )}
                </>
              }
              fullWidth
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              label="Quantity"
              size="small"
              type="number"
              value={part.quantity.toString()}
              onChange={(e) => setPart({ ...part, quantity: parseFloat(e.target.value)})}
              error={!!errors.quantity}
              helperText={errors.quantity}
              fullWidth
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={disabled}>Cancel</Button>
        <Button onClick={handleSelect} disabled={!part.part || disabled}>Select</Button>
      </DialogActions>
    </Dialog>
  );
}

function PartRow({ index, part, handleFieldChange, handleCurrencyFieldChange, deletePart, movePart, beforeRedirect, currency, currencyRate, disabled }) {
  const dragRef = useRef(null);
  const previewRef = useRef(null);

  const [{ handlerId }, drop] = useDrop({
    accept: 'part',
    collect(monitor) {
      return {
        handlerId: monitor.getHandlerId(),
      };
    },
    hover(item, monitor) {
      if (!previewRef.current) {
        return;
      }
      const dragIndex = item.index;
      const hoverIndex = index;

      if (dragIndex === hoverIndex) {
        return;
      }

      const hoverBoundingRect = previewRef.current?.getBoundingClientRect();
      const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
      const clientOffset = monitor.getClientOffset();
      const hoverClientY = clientOffset.y - hoverBoundingRect.top;
      
      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return;
      }
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return;
      }
      
      movePart(dragIndex, hoverIndex);
      item.index = hoverIndex;
    },
  });

  const [{ opacity }, drag, preview] = useDrag({
    type: 'part',
    item: () => {
      return { id: part.id, index };
    },
    collect: (monitor) => ({
      opacity: monitor.isDragging() ? 0 : 1,
    }),
  });

  drag(dragRef);
  drop(preview(previewRef));

  const navigate = useNavigate();

  const onRedirect = ()=>{
    if(beforeRedirect){
      beforeRedirect();
    }
    navigate(`/partComponents/edit/${part.part}`);
  }

  return (
    <TableRow style={{ opacity }} ref={previewRef} data-handler-id={handlerId}>
      <TableCell ref={dragRef} style={{ cursor: 'grab', width: '30px', padding: '4px' }}>{index + 1}</TableCell>
      <TableCell style={{ width: '15%', padding: '4px' }}>{part.manuf_part_number}</TableCell>
      <TableCell style={{ width: '25%', padding: '4px' }}>
        <TextField
          name="part_name"
          size="small"
          value={part.part_name}
          onChange={handleFieldChange}
          placeholder="Description"
          InputLabelProps={{ shrink: true }}
          fullWidth
          multiline
          disabled={disabled}
          InputProps={{
            ...(!disabled && {
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={onRedirect} edge="end" disabled={disabled} size="small">
                    <LinkIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }),
          }}
          error={part.errors && !!part.errors['part_name']}
          helperText={part.errors && part.errors['part_name']}
        />
      </TableCell>
      <TableCell style={{ width: '8%', padding: '4px' }}>
        <TextField
          name="quantity"
          size="small"
          value={part.quantity}
          onChange={handleFieldChange}
          placeholder="quantity"
          InputLabelProps={{ shrink: true }}
          fullWidth
          disabled={disabled}
          error={part.errors && !!part.errors['quantity']}
          helperText={part.errors && part.errors['quantity']}
        />
      </TableCell>
      <TableCell style={{ width: '10%', padding: '4px' }}>
        <TextField
          name="price"
          size="small"
          value={part.price_UI !== undefined ? part.price_UI : part.price / (currencyRate || 1)}
          onChange={handleCurrencyFieldChange}
          placeholder="price"
          InputLabelProps={{ shrink: true }}
          fullWidth
          disabled={disabled}
          error={part.errors && !!part.errors['price']}
          helperText={part.errors && part.errors['price']}
        />
      </TableCell>
      <TableCell style={{ width: '5%', padding: '4px' }}>
        {part.unit}
      </TableCell>
      <TableCell style={{ textAlign: 'right', width: '10%', padding: '4px' }}>
        {priceFormat((part.price_UI !== undefined ? part.price_UI : part.price / (currencyRate || 1)) * part.quantity, 2, 4, currency === 'USD' ? '$' : currency)}
      </TableCell>
      <TableCell style={{ width: '20%', padding: '4px' }}>
        <TextField
          name="remarks"
          size="small"
          value={part.remarks || ''}
          onChange={handleFieldChange}
          placeholder="Remarks"
          InputLabelProps={{ shrink: true }}
          fullWidth
          multiline
          disabled={disabled}
          error={part.errors && !!part.errors['remarks']}
          helperText={part.errors && part.errors['remarks']}
        />
      </TableCell>
      {
        !disabled && (
          <TableCell style={{ width: '30px', padding: '4px' }}>
            <IconButton onClick={deletePart} size="small">
              <DeleteIcon fontSize="small" />
            </IconButton>
          </TableCell>
        )
      }
    </TableRow>
  );
}

export default function PurchaseOrderParts(props) {
  const { parts, updateParts, beforeRedirect, currency, currencyRate, disabled = false } = props;

  const [localParts, setLocalParts] = useState(parts);
  const [open, setOpen] = useState(false);

  // Update base prices when currency rate changes
  useEffect(() => {
    if (localParts && localParts.length > 0) {
      const updatedParts = localParts.map(part => ({
        ...part,
        price: formatPriceToFloat((parseFloat(part.price_UI) || 0) * (currencyRate || 1), 2, 6)
      }));
      setLocalParts(updatedParts);
      updateParts(updatedParts);
    }
  }, [currencyRate]);

  const addPart = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const deletePart = (index) => () => {
    const newParts = localParts.filter((_, i) => i !== index);
    setLocalParts(newParts);
    updateParts(newParts);
  };

  const validateParts = (partsToValidate) => {
    const newParts = [];
    for (const part of partsToValidate) {
      part.errors = {};
      const priceValue = part.price !== undefined && part.price !== null ? part.price + '' : '';
      
      if (priceValue === '' || !validator.isFloat(priceValue)) {
        part.errors['price'] = 'Please enter a valid price';
      }
      if ( part.quantity <= 0 ) {
        part.errors['quantity'] = 'Please enter a valid quantity';
      }
      if ( part.part_name === '' || part.part_name === null ) {
        part.errors['part_name'] = 'Please enter a valid part name';
      }
      newParts.push(part);
    }
    return newParts;
  };

  const handleCurrencyFieldChange = (index) => (event) => {
    const key = event.target.name;
    let value = event.target.value;

    // When price is changed, update both price_UI and the base price (in USD)
    if (key === 'price') {
      const newPrice = formatPriceToFloat((parseFloat(value) || 0) * (currencyRate || 1), 2, 6);
      const newParts = validateParts(localParts.map((item, i) =>
        i === index ? { ...item, price: newPrice, price_UI: value } : item
      ));
      setLocalParts(newParts);
      updateParts(newParts);
    }
  };

  const handleFieldChange = (index) => (event, convertFunc) => {
    const key = event.target.name;
    let value = event.target.inputMode === 'numeric'
      ? parseFloat(event.target.value.replace(/\D/g, '') || 0)
      : event.target.value;

    if (convertFunc) {
      value = convertFunc(value);
    }

    const newParts = validateParts(localParts.map((item, i) => i === index ? { ...item, [key]: value } : item));
    setLocalParts(newParts);
    updateParts(newParts);
  };

  const handleSelect = (part) => {
    const newPart = {
      id: Math.floor(Math.random() * 1000000),
      ...part
    };
    const newParts = validateParts([...localParts, newPart]);
    setLocalParts(newParts);
    updateParts(newParts);
  };

  const movePart = useCallback((dragIndex, hoverIndex) => {
    const newParts = validateParts(
      update(localParts, {
        $splice: [
          [dragIndex, 1],
          [hoverIndex, 0, localParts[dragIndex]],
        ],
      }),
    );
    setLocalParts(newParts);
    updateParts(newParts);
  }, [localParts, updateParts]);

  return (
    <div>
      {
        !disabled && (
          <Button
            size="small"
            variant="contained"
            color="info"
            sx={{ margin: '10px 0' }}
            startIcon={<AddIcon />}
            onClick={addPart}
            disabled={disabled}
          >
            Add Manufacturer Part
          </Button>
        )}
      <DndProvider backend={HTML5Backend}>
        {localParts.length === 0
          ? <div style={{ textAlign: 'center' }}>This purchase order has no parts</div>
          : <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell style={{ width: '30px', padding: '4px' }}>#</TableCell>
                  <TableCell style={{ padding: '4px' }}>Part #</TableCell>
                  <TableCell style={{ padding: '4px' }}>Description</TableCell>
                  <TableCell style={{ padding: '4px' }}>Quantity</TableCell>
                  <TableCell style={{ padding: '4px' }}>Unit Price</TableCell>
                  <TableCell style={{ padding: '4px' }}>Unit</TableCell>
                  <TableCell style={{ textAlign: 'right', padding: '4px' }}>Total</TableCell>
                  <TableCell style={{ padding: '4px' }}>Remarks</TableCell>
                  <TableCell style={{ width: '30px', padding: '4px' }}></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {localParts.map((p, i) =>
                  <PartRow
                    key={p.id}
                    index={i}
                    part={p}
                    handleFieldChange={handleFieldChange(i)}
                    handleCurrencyFieldChange={handleCurrencyFieldChange(i)}
                    deletePart={deletePart(i)}
                    movePart={movePart}
                    beforeRedirect={beforeRedirect}
                    currency={currency}
                    currencyRate={currencyRate}
                    disabled={disabled}
                  />
                )}
                {localParts.length > 0 && (
                  <TableRow>
                    <TableCell colSpan={6} style={{ textAlign: 'right', fontWeight: 'bold', padding: '8px 4px' }}>
                      Grand Total:
                    </TableCell>
                    <TableCell style={{ textAlign: 'right', fontWeight: 'bold', padding: '8px 4px' }}>
                      {priceFormat(
                        localParts.reduce((sum, part) => 
                          sum + ((part.price_UI !== undefined ? part.price_UI : part.price / (currencyRate || 1)) * part.quantity), 
                        0
                      ), 2, 4, currency === 'USD' ? '$' : currency)}
                    </TableCell>
                    <TableCell colSpan={disabled ? 1 : 2} />
                  </TableRow>
                )}
              </TableBody>
            </Table>
        }
      </DndProvider>
      <SelectPartDialog
        open={open}
        onSelect={handleSelect}
        onClose={handleClose}
        currency={currency}
        currencyRate={currencyRate}
        disabled={disabled}
      />
    </div>
  );
} 